import { ChoosePeriodModal } from "@/components/date-filter/ChoosePeriodModal.tsx";
import { dateToPartialDate } from "@/components/date-filter/date-filter-presets.ts";
import {
  ApiDateFilter,
  ApiDateFilterMode,
  ApiDateFilterOperator,
} from "@/components/date-filter/date-filter.type.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ChooseDateModal } from "@/pages/reporting/employee/people-analytics/components/ChooseDateModal.tsx";
import {
  ApiPeopleAnalyticsTimeScope,
  ApiPeopleAnalyticsTimeScopeMode,
} from "@/types/reporting/people-analytics.types.ts";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

interface PeriodSelectorProps {
  timeScope: ApiPeopleAnalyticsTimeScope;
  onChange: (timeScope: ApiPeopleAnalyticsTimeScope) => void;
}

// Дата в URL и в запросе — YYYY-MM-DD без времени: срез задаётся календарным днём
function toApiDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

function parseApiDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function PeriodSelector({ timeScope, onChange }: PeriodSelectorProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { renderDate, buildFromPartialDate } = useFormatter();

  const dateAt = parseApiDate(timeScope.date_at);
  const dateFrom = parseApiDate(timeScope.date_from);
  const dateTo = parseApiDate(timeScope.date_to);

  const modeTitles: Record<ApiPeopleAnalyticsTimeScopeMode, string> = {
    [ApiPeopleAnalyticsTimeScopeMode.current]: t("reporting:people_analytics_scope_current"),
    [ApiPeopleAnalyticsTimeScopeMode.as_of]: t("reporting:people_analytics_scope_as_of"),
    [ApiPeopleAnalyticsTimeScopeMode.period]: t("reporting:people_analytics_scope_period"),
  };

  // Пока даты не выбраны, запрос ушёл бы с 422 — подставляем разумные значения по умолчанию
  const handleModeChange = (mode: ApiPeopleAnalyticsTimeScopeMode) => {
    if (mode === ApiPeopleAnalyticsTimeScopeMode.current) {
      onChange({ mode });
      return;
    }
    if (mode === ApiPeopleAnalyticsTimeScopeMode.as_of) {
      onChange({ mode, date_at: timeScope.date_at ?? toApiDate(new Date()) });
      return;
    }
    const today = new Date();
    const yearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    onChange({
      mode,
      date_from: timeScope.date_from ?? toApiDate(yearAgo),
      date_to: timeScope.date_to ?? toApiDate(today),
    });
  };

  const openDate = () => {
    openModal(
      ChooseDateModal,
      {
        value: dateAt,
        onSave: (next: Date | null) =>
          onChange({
            mode: ApiPeopleAnalyticsTimeScopeMode.as_of,
            date_at: next ? toApiDate(next) : null,
          }),
      },
      { size: "md" }
    );
  };

  const openRange = () => {
    const value: ApiDateFilter = {
      mode: ApiDateFilterMode.date,
      operator: ApiDateFilterOperator.custom,
      value_from: dateFrom ? dateToPartialDate(dateFrom, ApiDateFilterMode.date) : undefined,
      value_to: dateTo ? dateToPartialDate(dateTo, ApiDateFilterMode.date) : undefined,
    };
    openModal(
      ChoosePeriodModal,
      {
        value,
        availableModes: [ApiDateFilterMode.date],
        onSave: (next: ApiDateFilter | undefined) => {
          const from = buildFromPartialDate(next?.value_from ?? undefined);
          const to = buildFromPartialDate(next?.value_to ?? undefined);
          onChange({
            mode: ApiPeopleAnalyticsTimeScopeMode.period,
            date_from: from ? toApiDate(from) : null,
            date_to: to ? toApiDate(to) : null,
          });
        },
      },
      { size: "md" }
    );
  };

  const dateLabel = dateAt ? renderDate(dateAt) : t("reporting:people_analytics_scope_choose_date");

  const rangeLabel =
    dateFrom && dateTo
      ? `${renderDate(dateFrom)} – ${renderDate(dateTo)}`
      : t("reporting:people_analytics_scope_choose_period");

  return (
    <Stack gap="sm" className="items-start">
      <DropdownMenu
        trigger={
          <Button
            variant="secondary"
            size="sm"
            className="max-w-full"
            rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
          >
            {modeTitles[timeScope.mode]}
          </Button>
        }
      >
        <DropdownMenuItem
          title={t("reporting:people_analytics_scope_current")}
          isSelected={timeScope.mode === ApiPeopleAnalyticsTimeScopeMode.current}
          onClick={() => handleModeChange(ApiPeopleAnalyticsTimeScopeMode.current)}
        />
        <DropdownMenuItem
          title={t("reporting:people_analytics_scope_as_of")}
          description={t("reporting:people_analytics_scope_as_of_desc")}
          isSelected={timeScope.mode === ApiPeopleAnalyticsTimeScopeMode.as_of}
          onClick={() => handleModeChange(ApiPeopleAnalyticsTimeScopeMode.as_of)}
        />
        <DropdownMenuItem
          title={t("reporting:people_analytics_scope_period")}
          description={t("reporting:people_analytics_scope_period_desc")}
          isSelected={timeScope.mode === ApiPeopleAnalyticsTimeScopeMode.period}
          onClick={() => handleModeChange(ApiPeopleAnalyticsTimeScopeMode.period)}
        />
      </DropdownMenu>

      {timeScope.mode === ApiPeopleAnalyticsTimeScopeMode.as_of && (
        <Button
          variant="secondary"
          size="sm"
          className="max-w-full"
          rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
          onClick={openDate}
        >
          {dateLabel}
        </Button>
      )}

      {timeScope.mode === ApiPeopleAnalyticsTimeScopeMode.period && (
        <Button
          variant="secondary"
          size="sm"
          className="max-w-full"
          rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
          onClick={openRange}
        >
          {rangeLabel}
        </Button>
      )}
    </Stack>
  );
}
